
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card } from '@/components/ui/card';
import { ChatLogsList, type ChatLog } from './ChatLogsList';
import { ChatLogsFilter } from './ChatLogsFilter';
import { DateRangeFilter } from '@/components/admin/DateRangeFilter';
import { format, subDays } from 'date-fns';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { toast } from '@/components/ui/use-toast';

export function ChatLogsContent() {
  const [dateRange, setDateRange] = useState({
    from: subDays(new Date(), 7),
    to: new Date(),
  });
  const [studentFilter, setStudentFilter] = useState('');
  const { adminUser, isAdmin } = useAdminAuth();
  
  const formattedFromDate = format(dateRange.from, 'yyyy-MM-dd');
  const formattedToDate = format(dateRange.to, 'yyyy-MM-dd');
  
  console.log('Auth state:', { isAdmin, adminUserId: adminUser?.id });
  console.log('Date range:', { from: formattedFromDate, to: formattedToDate });
  
  const { data: chatLogs, isLoading, error, refetch } = useQuery({
    queryKey: ['chat-logs', formattedFromDate, formattedToDate, studentFilter, isAdmin],
    queryFn: async () => {
      if (!isAdmin) {
        console.error('User is not authenticated as admin');
        return [];
      }
      
      console.log('Fetching chat logs with filter:', studentFilter);
      
      // Step 1: Query the chat_logs table without joining with profiles
      let query = supabase
        .from('chat_logs')
        .select('*')
        .gte('timestamp', formattedFromDate)
        .lte('timestamp', formattedToDate)
        .order('timestamp', { ascending: false });
      
      if (studentFilter) {
        query = query.textSearch('message', studentFilter);
      }
      
      const { data, error } = await query;
      
      if (error) {
        console.error('Error fetching chat logs:', error);
        toast.error(error.message);
        throw error;
      }

      console.log('Chat logs fetched:', data?.length || 0);

      // Transform the data to match the ChatLog type
      const logs = data ? data.map(log => ({
        id: log.id,
        user_id: log.user_id || '',
        message: log.message || '',
        response: log.response || '',
        created_at: log.timestamp || '',
        timestamp: log.timestamp || '',
        user_name: 'Anonymous User' // Default to Anonymous User
      })) as ChatLog[] : [];
      
      // Step 2: For logs with a user_id, try to fetch their names
      const userIds = logs
        .filter(log => log.user_id)
        .map(log => log.user_id)
        .filter((id, index, self) => self.indexOf(id) === index); // Get unique user IDs

      if (userIds.length > 0) {
        console.log('Fetching user names for', userIds.length, 'users');
        const { data: profiles, error: profilesError } = await supabase
          .from('profiles')
          .select('id, full_name')
          .in('id', userIds);
          
        if (profilesError) {
          console.error('Error fetching profiles:', profilesError);
        }
        
        if (profiles && profiles.length > 0) {
          console.log('Retrieved profiles:', profiles.length);
          // Create a map of user_id -> full_name for quick lookups
          const userNameMap = profiles.reduce((map, profile) => {
            map[profile.id] = profile.full_name || 'Unknown User';
            return map;
          }, {});
          
          // Update log entries with user names where available
          logs.forEach(log => {
            if (log.user_id && userNameMap[log.user_id]) {
              log.user_name = userNameMap[log.user_id];
            }
          });
        }
      }
      
      return logs;
    },
    enabled: isAdmin, // Only run query if user is admin
  });
  
  return (
    <div className="space-y-6">
      {error && (
        <div className="p-4 border border-red-300 bg-red-50 text-red-800 rounded-md">
          An error occurred while fetching chat logs: {error instanceof Error ? error.message : 'Unknown error'}
        </div>
      )}
      
      <div className="grid gap-4 md:grid-cols-2">
        <Card className="p-4">
          <h3 className="text-lg font-medium mb-2">Date Range</h3>
          <DateRangeFilter 
            dateRange={dateRange} 
            onDateRangeChange={setDateRange} 
          />
        </Card>
        
        <Card className="p-4">
          <h3 className="text-lg font-medium mb-2">Search</h3>
          <ChatLogsFilter 
            value={studentFilter} 
            onChange={setStudentFilter}
          />
        </Card>
      </div>
      
      <ChatLogsList 
        chatLogs={chatLogs || []} 
        isLoading={isLoading}
        refetch={refetch}
      />
    </div>
  );
}
